/**
 * Advanced search syntax in the UI (docs/concepts/concept_advanced_search_syntax.md §5):
 * the live preview in the search bar dropdown, the summary row on the results
 * page and the exact-phrase hint.
 *
 * Chips: filled = condition (must match), neutral = search words (ranked as
 * today), outlined after NOT = excluded, dashed = still being typed.
 */

import { Fragment, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  DEFAULT_MAX_CHIPS,
  DEFAULT_MAX_PHRASE_WORDS,
  fixQuery,
  parseQuery,
  removeChip,
  type Chip as SyntaxChip,
  type ParsedQuery,
  type SyntaxOperator,
  type SyntaxWarning,
} from '../../searchCore/querySyntax';

const DASHED = {
  bgcolor: 'transparent',
  border: '1px dashed',
  borderColor: 'outline',
  color: 'text.secondary',
} as const;

function chipLabel(chip: SyntaxChip, live: boolean): string {
  if (chip.kind !== 'phrase') return chip.text;
  return live && chip.open ? `“${chip.text} …` : `“${chip.text}”`;
}

function OperatorLabel({ operator }: { operator: SyntaxOperator }) {
  return operator === 'OR' ? (
    <Typography component="span" variant="body2" sx={{ color: 'primary.main', fontWeight: 600, px: 0.5 }}>
      OR
    </Typography>
  ) : (
    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
      {operator}
    </Typography>
  );
}

function Group({ boxed, children }: { boxed: boolean; children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        ...(boxed && { p: 0.5, border: '1px dashed', borderColor: 'outline', borderRadius: 1 }),
      }}
    >
      {children}
    </Box>
  );
}

interface QueryChipsProps {
  parsed: ParsedQuery;
  /** While typing: open phrases and trailing operators are shown dashed. */
  live: boolean;
  onRemove?: (group: number, chip: number) => void;
  /** Results page: a trailing operator offers "+ Add word". */
  onAddWord?: () => void;
}

function QueryChips({ parsed, live, onRemove, onAddWord }: QueryChipsProps) {
  const { t } = useTranslation('search');
  const trailing = parsed.trailingOperator;
  const last = parsed.groups.length - 1;

  const renderChip = (chip: SyntaxChip, group: number, index: number) => {
    const label = chipLabel(chip, live);
    const pending = live && chip.open;
    const ariaKey = pending
      ? 'syntax.chip.pending'
      : chip.negated
        ? 'syntax.chip.excluded'
        : chip.hard
          ? 'syntax.chip.mustMatch'
          : 'syntax.chip.searchWords';
    const sx = pending
      ? DASHED
      : chip.negated
        ? { bgcolor: 'transparent', border: '1px solid', borderColor: 'outlineVariant', color: 'text.secondary' }
        : chip.hard
          ? { bgcolor: 'secondaryContainer', color: 'onSecondaryContainer' }
          : { bgcolor: 'surfaceContainerHighest', color: 'text.primary' };
    return (
      <Chip
        size="small"
        label={label}
        aria-label={t(ariaKey, { text: label })}
        onDelete={onRemove ? () => onRemove(group, index) : undefined}
        deleteIcon={onRemove ? <CloseIcon titleAccess={t('syntax.chip.remove', { text: label })} /> : undefined}
        sx={{ ...sx, maxWidth: '100%' }}
      />
    );
  };

  let tail: ReactNode = null;
  if (trailing && live) {
    tail = <Chip size="small" label="…" aria-label={t('syntax.chip.waiting')} sx={DASHED} />;
  } else if (trailing && onAddWord) {
    tail = (
      <Chip
        size="small"
        clickable
        onClick={onAddWord}
        label={t(trailing === 'NOT' ? 'syntax.addWordNot' : 'syntax.addWord')}
        aria-label={t('syntax.addWordLabel', { operator: trailing })}
        sx={{ ...DASHED, color: 'primary.main' }}
      />
    );
  }
  const boxed = parsed.groups.length > 1 || (trailing === 'OR' && tail !== null);
  const groupTail = trailing && trailing !== 'OR' && tail ? (
    <>
      <OperatorLabel operator={trailing} />
      {tail}
    </>
  ) : null;

  return (
    <Box
      role="group"
      aria-label={t('syntax.conditionsLabel')}
      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}
    >
      {parsed.groups.map((group, g) => (
        <Fragment key={g}>
          {g > 0 && <OperatorLabel operator="OR" />}
          <Group boxed={boxed}>
            {group.map((chip, i) => (
              <Fragment key={i}>
                {chip.negated ? (
                  <OperatorLabel operator="NOT" />
                ) : i > 0 && chip.andBefore ? (
                  <OperatorLabel operator="AND" />
                ) : null}
                {renderChip(chip, g, i)}
              </Fragment>
            ))}
            {g === last && groupTail}
          </Group>
        </Fragment>
      ))}
      {parsed.groups.length === 0 && groupTail}
      {trailing === 'OR' && tail && (
        <>
          <OperatorLabel operator="OR" />
          <Group boxed>{tail}</Group>
        </>
      )}
    </Box>
  );
}

function hintText(t: TFunction<'search'>, warning: SyntaxWarning): string {
  switch (warning.code) {
    case 'lowercase_operator':
      return t('syntax.hints.lowercase_operator.text', { word: warning.word, upper: warning.word.toUpperCase() });
    case 'dangling_operator':
      return warning.operator === 'NOT'
        ? t('syntax.hints.dangling_operator.textNot')
        : t('syntax.hints.dangling_operator.text', { operator: warning.operator });
    case 'too_many_chips':
      return t('syntax.hints.too_many_chips.text', { max: DEFAULT_MAX_CHIPS });
    case 'phrase_too_long':
      return t('syntax.hints.phrase_too_long.text', { max: DEFAULT_MAX_PHRASE_WORDS });
    default:
      return t(`syntax.hints.${warning.code}.text`);
  }
}

function fixLabel(t: TFunction<'search'>, warning: SyntaxWarning): string {
  if (warning.code === 'lowercase_operator') {
    return t('syntax.hints.lowercase_operator.fix', { upper: warning.word.toUpperCase() });
  }
  if (warning.code === 'dangling_operator') {
    return t('syntax.hints.dangling_operator.fix', { operator: warning.operator });
  }
  return t(`syntax.hints.${warning.code}.fix`);
}

/**
 * Hints shown as text rows. Trailing operators and open phrases are shown as
 * dashed chips instead; on the results page lowercase operators and closed
 * quotes are short notes next to the chips.
 */
function isHintRow(warning: SyntaxWarning, live: boolean): boolean {
  if (warning.code === 'dangling_operator' && warning.trailing) return false;
  if (warning.code === 'quote_closed') return false;
  if (!live && warning.code === 'lowercase_operator') return false;
  return true;
}

interface QueryHintsProps {
  parsed: ParsedQuery;
  query: string;
  live: boolean;
  onFix?: (fixed: string) => void;
}

function QueryHints({ parsed, query, live, onFix }: QueryHintsProps) {
  const { t } = useTranslation('search');
  const rows = parsed.warnings.filter((w) => isHintRow(w, live));
  if (rows.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {rows.map((warning, index) => {
        const fixed = onFix ? fixQuery(query, warning) : null;
        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {hintText(t, warning)}
            </Typography>
            {fixed !== null && onFix && (
              <Button
                size="small"
                // Keep the focus in the search bar while typing.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onFix(fixed)}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {fixLabel(t, warning)}
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

interface QuerySyntaxPreviewProps {
  query: string;
  id?: string;
  onFix: (fixed: string) => void;
}

/** "Searching for results with" panel in the search bar dropdown (concept §5.2). */
export function QuerySyntaxPreview({ query, id, onFix }: QuerySyntaxPreviewProps) {
  const { t } = useTranslation('search');
  const parsed = useMemo(() => parseQuery(query), [query]);
  const empty = parsed.groups.length === 0 && !parsed.trailingOperator;
  return (
    <>
      <Divider />
      <Box id={id} sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('syntax.previewTitle')}
        </Typography>
        {empty ? (
          <Typography variant="body2" color="text.secondary">
            –
          </Typography>
        ) : (
          <QueryChips parsed={parsed} live />
        )}
        <QueryHints parsed={parsed} query={query} live onFix={onFix} />
      </Box>
    </>
  );
}

interface QuerySyntaxSummaryProps {
  /** The searched query (not unsubmitted edits in the search bar). */
  query: string;
  /** Search again (chip removed, fix applied). Without it the chips are read-only. */
  onSearch?: (query: string) => void;
  /** Put text into the search bar and focus it. */
  onEditQuery?: (query: string) => void;
  /** Before the chips, e.g. "6 results match". */
  lead?: ReactNode;
  /** After the chips and notes, e.g. the "limited results" chip. */
  trail?: ReactNode;
}

/** "N results match" + condition chips above the results (concept §5.3). */
export function QuerySyntaxSummary({ query, onSearch, onEditQuery, lead, trail }: QuerySyntaxSummaryProps) {
  const { t } = useTranslation('search');
  const parsed = useMemo(() => parseQuery(query), [query]);

  const notes: string[] = [];
  if (parsed.warnings.some((w) => w.code === 'quote_closed')) notes.push(t('syntax.notes.quoteClosed'));
  const ignored = [
    ...new Set(parsed.warnings.flatMap((w) => (w.code === 'lowercase_operator' ? [w.word.toLowerCase()] : []))),
  ];
  if (ignored.length > 0) {
    notes.push(t('syntax.notes.ignored', { words: ignored.map((word) => `“${word}”`).join(', ') }));
  }

  const handleRemove = onSearch
    ? (group: number, chip: number) => {
        const next = removeChip(query, group, chip);
        // Last chip removed: empty search field, the results stay until the next search.
        if (next) onSearch(next);
        else onEditQuery?.('');
      }
    : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {lead}
        <QueryChips
          parsed={parsed}
          live={false}
          onRemove={handleRemove}
          onAddWord={onEditQuery ? () => onEditQuery(`${query.trimEnd()} `) : undefined}
        />
        {notes.map((note) => (
          <Typography key={note} variant="body2" color="text.secondary">
            · {note}
          </Typography>
        ))}
        {trail}
      </Box>
      <QueryHints parsed={parsed} query={query} live={false} onFix={onSearch} />
    </Box>
  );
}

interface PhraseHintProps {
  /** The phrase including its quotes, e.g. `"alarm list"`. */
  phrase: string;
  count: number;
  onSearch: (query: string) => void;
}

/** "Looking for this exact phrase?" below the result count (concept §5.5). */
export function PhraseHint({ phrase, count, onSearch }: PhraseHintProps) {
  const { t } = useTranslation('search');
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {t('syntax.phraseHint.question')}{' '}
      <Link component="button" type="button" onClick={() => onSearch(phrase)} sx={{ verticalAlign: 'baseline' }}>
        {t('syntax.phraseHint.action', { phrase: phrase.slice(1, -1) })}
      </Link>{' '}
      {t('syntax.phraseHint.count', { count })}
    </Typography>
  );
}
